/**
 * 设置弹窗组件
 *
 * 提供全局游戏设置：返回主菜单、清除存档、退出登录、版本检查更新。
 * 在所有需要确认的操作前显示二次确认。
 */

import React, { useState, useCallback } from 'react';
import { Settings, X, Home, Trash2, LogOut, AlertTriangle, RefreshCw, CheckCircle, AlertCircle, ArrowUpCircle } from 'lucide-react';
import { getVersionInfo, formatBuildTime, checkForUpdate } from '../utils/version';

// ── Types ────────────────────────────────────────────────────────────
interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Current game step — used to conditionally show "返回主菜单" */
  currentStep: string;
  onGoToMenu: () => void;
  onClearSave: () => void;
  onLogout: () => void;
}

// ── Confirm dialog (inline) ──────────────────────────────────────────
type ConfirmTarget = 'menu' | 'clear' | 'logout' | null;

const CONFIRM_CONFIG: Record<Exclude<ConfirmTarget, null>, { title: string; desc: string; btnText: string; btnColor: string }> = {
  menu: {
    title: '返回主菜单？',
    desc: '当前游戏进度将丢失。',
    btnText: '确认返回',
    btnColor: 'bg-amber-accent hover:bg-[#E5A03F]',
  },
  clear: {
    title: '清除所有存档？',
    desc: '所有游戏进度将被永久删除，无法恢复。',
    btnText: '确认清除',
    btnColor: 'bg-red-500 hover:bg-red-600',
  },
  logout: {
    title: '确认退出登录？',
    desc: '退出后需要重新登录才能继续游戏。',
    btnText: '确认退出',
    btnColor: 'bg-red-500 hover:bg-red-600',
  },
};

// ── Component ────────────────────────────────────────────────────────
const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  currentStep,
  onGoToMenu,
  onClearSave,
  onLogout,
}) => {
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget>(null);

  // ── Update check state ──────────────────────────────────────────────
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'hasUpdate' | 'latest' | 'error'>('idle');
  const [updateMessage, setUpdateMessage] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setConfirmTarget(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (confirmTarget === 'menu') onGoToMenu();
    else if (confirmTarget === 'clear') onClearSave();
    else if (confirmTarget === 'logout') onLogout();
    setConfirmTarget(null);
    onClose();
  }, [confirmTarget, onGoToMenu, onClearSave, onLogout, onClose]);

  // ── Update checking ─────────────────────────────────────────────────
  const clearCacheAndReload = useCallback(async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) await reg.unregister();
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      window.location.reload();
    } catch {
      window.location.reload();
    }
  }, []);

  const handleCheckUpdate = useCallback(async () => {
    setIsCheckingUpdate(true);
    setUpdateMessage(null);
    setUpdateStatus('idle');

    try {
      // Try to update Service Worker if present (with timeout)
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          try {
            await Promise.race([
              reg.update(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
            ]);
          } catch { /* ignore SW timeout */ }
        }
      }

      const result = await checkForUpdate();
      if (result.hasUpdate) {
        setUpdateStatus('hasUpdate');
        setUpdateMessage(
          `发现新版本：${result.currentCommit.slice(0, 7)} → ${result.latestCommit?.slice(0, 7)}`
        );
      } else {
        setUpdateStatus('latest');
        setUpdateMessage('已是最新版本');
      }
    } catch {
      setUpdateStatus('error');
      setUpdateMessage('检查更新失败');
    } finally {
      setIsCheckingUpdate(false);
      setTimeout(() => {
        setUpdateMessage(null);
        setUpdateStatus('idle');
      }, 8000);
    }
  }, []);

  if (!isOpen) return null;

  const showGoToMenu = currentStep !== 'MENU';
  const cfg = confirmTarget ? CONFIRM_CONFIG[confirmTarget] : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal card */}
      <div
        className="relative w-80 max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-[#E5E0D8] overflow-hidden animate-[slideUp_0.25s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2">
            <Settings size={18} className="text-[#9CA3AF]" />
            <h2 className="font-bold text-lg text-[#2D2D2D]">设置</h2>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5EDE3] transition-colors text-[#9CA3AF] hover:text-[#6B7280]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="h-px bg-[#E5E0D8] mx-5" />

        {/* Body */}
        <div className="p-5 space-y-2">
          {/* Confirm overlay */}
          {cfg ? (
            <div className="py-2 space-y-4 animate-[fadeIn_0.15s_ease-out]">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertTriangle size={18} className="text-amber-accent" />
                </div>
                <div>
                  <p className="font-bold text-[#2D2D2D] text-sm">{cfg.title}</p>
                  <p className="text-xs text-[#9CA3AF] mt-1">{cfg.desc}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmTarget(null)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium text-[#6B7280] bg-[#F5EDE3] hover:bg-[#EDE5D8] transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirm}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-colors ${cfg.btnColor}`}
                >
                  {cfg.btnText}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* 返回主菜单 */}
              {showGoToMenu && (
                <SettingsItem
                  icon={<Home size={18} />}
                  label="返回主菜单"
                  sublabel="放弃当前对局，回到首页"
                  onClick={() => setConfirmTarget('menu')}
                />
              )}

              {/* 清除存档 */}
              <SettingsItem
                icon={<Trash2 size={18} />}
                label="清除存档"
                sublabel="删除所有本地游戏数据"
                onClick={() => setConfirmTarget('clear')}
                danger
              />

              {/* 退出登录 */}
              <SettingsItem
                icon={<LogOut size={18} />}
                label="退出登录"
                sublabel="退出当前账号"
                onClick={() => setConfirmTarget('logout')}
                danger
              />

              {/* ── 版本信息 ────────────────────────────────── */}
              <VersionSection
                isCheckingUpdate={isCheckingUpdate}
                updateStatus={updateStatus}
                updateMessage={updateMessage}
                onCheckUpdate={handleCheckUpdate}
                onApplyUpdate={clearCacheAndReload}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-4 pt-1">
          <div className="h-px bg-[#E5E0D8] mb-3" />
          <p className="text-center text-[10px] text-[#C4B9AC]">
            赛博红娘 · Powered by Wortou AI
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;

// ── Sub-components ───────────────────────────────────────────────────

interface SettingsItemProps {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  onClick: () => void;
  danger?: boolean;
}

const SettingsItem: React.FC<SettingsItemProps> = ({ icon, label, sublabel, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-left group ${
      danger
        ? 'hover:bg-red-50 text-[#2D2D2D]'
        : 'hover:bg-[#FFF3E0] text-[#2D2D2D]'
    }`}
  >
    <div className={`shrink-0 ${danger ? 'text-red-400 group-hover:text-red-500' : 'text-[#9CA3AF] group-hover:text-amber-accent'}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-medium ${danger ? 'group-hover:text-red-600' : ''}`}>{label}</p>
      <p className="text-[11px] text-[#9CA3AF]">{sublabel}</p>
    </div>
  </button>
);

// ── Version section ──────────────────────────────────────────────────

interface VersionSectionProps {
  isCheckingUpdate: boolean;
  updateStatus: 'idle' | 'hasUpdate' | 'latest' | 'error';
  updateMessage: string | null;
  onCheckUpdate: () => void;
  onApplyUpdate: () => void;
}

const VersionSection: React.FC<VersionSectionProps> = ({
  isCheckingUpdate,
  updateStatus,
  updateMessage,
  onCheckUpdate,
  onApplyUpdate,
}) => {
  const versionInfo = getVersionInfo();
  const commitDisplay = versionInfo.commit === 'local' ? 'local' : versionInfo.commit;

  return (
    <div className="mt-3 pt-3 border-t border-[#E5E0D8]">
      {/* Section header */}
      <p className="text-[10px] font-bold text-[#C4B9AC] uppercase tracking-wider px-4 mb-2">
        版本信息
      </p>

      {/* Version details */}
      <div className="px-4 py-2 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#9CA3AF]">版本</span>
          <span className="text-xs font-medium text-[#6B7280]">
            v{versionInfo.version}
            <span className="text-[#C4B9AC] ml-1">({commitDisplay})</span>
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#9CA3AF]">构建时间</span>
          <span className="text-xs text-[#6B7280]">{formatBuildTime(versionInfo.buildTime)}</span>
        </div>
      </div>

      {/* Update check button */}
      <div className="px-4 pt-2 pb-1">
        {updateStatus === 'hasUpdate' ? (
          <button
            onClick={onApplyUpdate}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-coral-500 to-amber-accent hover:from-coral-700 hover:to-coral-500 transition-all shadow-sm"
          >
            <ArrowUpCircle size={16} />
            立即更新
          </button>
        ) : (
          <button
            onClick={onCheckUpdate}
            disabled={isCheckingUpdate}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-[#6B7280] bg-[#F5EDE3] hover:bg-[#EDE5D8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={14} className={isCheckingUpdate ? 'animate-spin' : ''} />
            {isCheckingUpdate ? '检查中...' : '检查更新'}
          </button>
        )}
      </div>

      {/* Status message */}
      {updateMessage && (
        <div className={`mx-4 mt-2 px-3 py-2 rounded-lg text-xs flex items-center gap-2 animate-[fadeIn_0.15s_ease-out] ${
          updateStatus === 'hasUpdate'
            ? 'bg-coral-100 text-coral-700'
            : updateStatus === 'latest'
            ? 'bg-green-50 text-green-600'
            : 'bg-red-50 text-red-500'
        }`}>
          {updateStatus === 'hasUpdate' && <ArrowUpCircle size={14} />}
          {updateStatus === 'latest' && <CheckCircle size={14} />}
          {updateStatus === 'error' && <AlertCircle size={14} />}
          {updateMessage}
        </div>
      )}
    </div>
  );
};
